import { useCallback, useEffect, useMemo, useState } from 'react';

import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { isEqual } from 'lodash';
import { instanceOf, oneOf, shape } from 'prop-types';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';

import { ComparisonPeriodAfter, ComparisonPeriodBefore } from '../../icon/Icons';
import DateTime from '../time-range-filter/DateTime';
import { floorToMinute } from '../../../utilities/DateTimeUtilities';
import Filter from '../../page-framework/filter/FilterDecorator';
import { filterPropTypes, filterPropDefaults } from '../../page-framework/filter/FilterPropTypes';
import {
    filterId,
    AFTER_LABEL,
    BEFORE_LABEL,
    DEFAULT_OPTIONS,
    title,
    getComparisonPeriod,
    MAX_FULL_RANGE_MS,
    TIME_RANGE_COMPARISON_IDS,
    TIME_RANGE_COMPARISON_PERIODS,
} from './TimeRangeComparisonFilterConstants';
import TimeRangeComparisonFilterStateServiceFactory, {
    getComparisonPeriodTimeRange,
    getTimeZoneObject,
} from './TimeRangeComparisonFilterStateService';
import { TimeZoneAutocompleter } from './TimeZoneAutocompleter';
import ValidationError from '../../form/ValidationError';

export { filterId } from './TimeRangeComparisonFilterConstants';

const useStyles = makeStyles((theme) => ({
    row: {
        '& .MuiSvgIcon-root': {
            marginTop: theme.spacing(0.4),
            verticalAlign: 'middle',
        },
        '& .MuiInputBase-root': {
            marginLeft: theme.spacing(1),
            marginRight: theme.spacing(1),
            verticalAlign: 'middle',
        },
        '& .MuiSelect-root': {
            marginLeft: theme.spacing(1),
            marginRight: theme.spacing(1),
            verticalAlign: 'middle',
            minWidth: theme.spacing(12),
        },
        '& .MuiFormLabel-root': {
            marginLeft: theme.spacing(1),
        },
    },
    text: {
        display: 'inline-flex',
        marginTop: theme.spacing(0.6),
        fontSize: '1.2em',
        verticalAlign: 'middle',
        // poor man's horizontal alignment:
        minWidth: '3.0em',
    },
    dateTime: {
        marginLeft: theme.spacing(2),
    },
}));

function TimeRangeComparisonFilter(props) {
    const { state, filterActions, options } = props;
    const resolvedOptions = useMemo(
        () => ({
            ...DEFAULT_OPTIONS,
            ...options,
        }),
        [options]
    );

    const [beforeEnd, setBeforeEnd] = useState(state?.before?.end);
    const [afterStart, setAfterStart] = useState(state?.after?.start);

    const [beforePeriod, setBeforePeriod] = useState(state?.before?.id);
    const [afterPeriod, setAfterPeriod] = useState(state?.after?.id);

    const [timeZone, setTimeZone] = useState(state?.timeZone);

    const [beforeControlsValid, setBeforeControlsValid] = useState(true);
    const [afterControlsValid, setAfterControlsValid] = useState(true);

    const [validity, setValidity] = useState({
        minBefore: true,
        range: true,
        maxRange: true,
    });

    const classes = useStyles();

    useEffect(() => {
        if (!beforeEnd || !afterStart) return; // not initialized
        const beforeEndMs = beforeEnd.getTime();
        const beforeStartMs = beforeEnd.getTime() - getComparisonPeriod(beforePeriod).durationInMinutes * 60 * 1000;
        const afterEndMs = afterStart.getTime() + getComparisonPeriod(afterPeriod).durationInMinutes * 60 * 1000;
        const fullRangeMs = afterEndMs - beforeStartMs;

        setValidity((currentValidity) => {
            const newValidity = {
                ...currentValidity,
                minBefore: beforeEndMs > 0,
                range: beforeEnd.getTime() <= afterStart.getTime(),
                maxRange: fullRangeMs <= MAX_FULL_RANGE_MS,
            };
            return isEqual(currentValidity, newValidity) ? currentValidity : newValidity;
        });
    }, [beforeEnd, afterStart, beforePeriod, afterPeriod, resolvedOptions]);

    useEffect(() => {
        filterActions.setValid(validity.minBefore && validity.maxRange && validity.range);
        setBeforeControlsValid(validity.minBefore);
        setAfterControlsValid(validity.range && validity.maxRange);
    }, [validity, filterActions]);

    const handleBeforeEndChanged = useCallback(
        (newBeforeEnd) => {
            const rounded = floorToMinute(newBeforeEnd);
            setBeforeEnd(rounded);
            filterActions.setState({
                ...state,
                before: getComparisonPeriodTimeRange(getComparisonPeriod(state.before.id), null, rounded),
            });
        },
        [filterActions, state]
    );

    const handleAfterStartChanged = useCallback(
        (newAfterStart) => {
            const rounded = floorToMinute(newAfterStart);
            setAfterStart(rounded);
            filterActions.setState({
                ...state,
                after: getComparisonPeriodTimeRange(getComparisonPeriod(state.after.id), rounded, null),
            });
        },
        [filterActions, state]
    );

    const handleBeforePeriodChanged = useCallback(
        (event) => {
            const newBeforePeriod = event.target.value;
            setBeforePeriod(newBeforePeriod);

            filterActions.setState({
                ...state,
                before: getComparisonPeriodTimeRange(getComparisonPeriod(newBeforePeriod), null, state.before.end),
            });
        },
        [filterActions, state]
    );

    const handleAfterPeriodChanged = useCallback(
        (event) => {
            const newAfterPeriod = event.target.value;
            setAfterPeriod(newAfterPeriod);

            filterActions.setState({
                ...state,
                after: getComparisonPeriodTimeRange(getComparisonPeriod(newAfterPeriod), state.after.start, null),
            });
        },
        [filterActions, state]
    );

    const onTimeZoneChange = useCallback(
        (_event, data) => {
            const newTimeZone = getTimeZoneObject(data.name);
            setTimeZone(newTimeZone);
            filterActions.setState({
                ...state,
                timeZone: newTimeZone,
            });
        },
        [filterActions, state]
    );

    if (!resolvedOptions.enableCustom) return null;

    return (
        <>
            <div className={classes.row}>
                <ComparisonPeriodBefore className={classes.icon} />
                <FormControl>
                    <InputLabel id="before-period-select-label">
                        <Trans>Before Time Range</Trans>
                    </InputLabel>
                    <Select
                        labelId="before-period-select-label"
                        id="before-period-select"
                        value={beforePeriod}
                        onChange={(event) => handleBeforePeriodChanged(event)}
                    >
                        {TIME_RANGE_COMPARISON_PERIODS.map((timeRange) => {
                            return (
                                <MenuItem value={timeRange.id} key={timeRange.id} label={timeRange.name}>
                                    <Trans>{timeRange.name}</Trans>
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>

                <div className={classes.text}>{t`before`}</div>

                <DateTime
                    timeZone={timeZone.name}
                    error={!beforeControlsValid}
                    label={i18n._(BEFORE_LABEL)}
                    hideLabels
                    value={beforeEnd}
                    onChange={handleBeforeEndChanged}
                    onValidChange={(valid) => setBeforeControlsValid(valid)}
                    className={classes.dateTime}
                />
            </div>

            <div className={classes.row}>
                <ComparisonPeriodAfter className={classes.icon} />

                <FormControl>
                    <InputLabel id="after-period-select-label">
                        <Trans>After Time Range</Trans>
                    </InputLabel>
                    <Select
                        labelId="after-label"
                        id="after-select"
                        value={afterPeriod}
                        onChange={(event) => handleAfterPeriodChanged(event)}
                    >
                        {TIME_RANGE_COMPARISON_PERIODS.map((timeRange) => {
                            return (
                                <MenuItem value={timeRange.id} key={timeRange.id} label={timeRange.name}>
                                    <Trans>{timeRange.name}</Trans>
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
                <div className={classes.text}>{t`after`}</div>

                <DateTime
                    timeZone={timeZone.name}
                    error={!afterControlsValid}
                    label={i18n._(AFTER_LABEL)}
                    hideLabels
                    value={afterStart}
                    onChange={handleAfterStartChanged}
                    onValidChange={(valid) => setAfterControlsValid(valid)}
                    className={classes.dateTime}
                />
            </div>

            <TimeZoneAutocompleter
                label={<Trans>Time Zone</Trans>}
                required
                value={timeZone.name}
                onChange={onTimeZoneChange}
            />

            <ValidationError
                invalid={!validity.range}
                message={i18n._(t`The before date must be before the after date`)}
            />
            <ValidationError
                invalid={!validity.maxRange}
                message={i18n._(t`The total time range cannot be more than one year`)}
            />
            <ValidationError invalid={!validity.minBefore} message={i18n._(t`The before date cannot be before 1970`)} />
        </>
    );
}

TimeRangeComparisonFilter.propTypes = {
    ...filterPropTypes,
    state: shape({
        beforePeriod: oneOf(TIME_RANGE_COMPARISON_IDS),
        beforeEnd: instanceOf(Date),
        afterPeriod: oneOf(TIME_RANGE_COMPARISON_IDS),
        afterStart: instanceOf(Date),
    }),
};

TimeRangeComparisonFilter.defaultProps = {
    ...filterPropDefaults,
    id: filterId,
    title,
    autoRefresh: false,
    options: DEFAULT_OPTIONS,
    state: undefined,
    maxWidth: 'md',
    fullWidth: false,
    summaryMaxWidth: 80,
};

export default Filter({
    filterId,
    filterStateServiceFactory: TimeRangeComparisonFilterStateServiceFactory,
})(TimeRangeComparisonFilter);
